import { useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { apiErrorMessage, conflictJobId } from '@/api/errors';
import {
  hasKeyProblem,
  integrationsApi,
  type Integration,
  type IntegrationType,
} from '@/api/queries/integrations';
import {
  activeSyncJobsQueryKey,
  isJobActive,
  syncJobsApi,
  type SyncJob,
} from '@/api/queries/syncJobs';
import { segmentLinksApi, type Segment, type SegmentSyncRef } from '@/api/queries/workspace';
import { Button, Checkbox, Icon, IconButton, ModalRoot, useToast, type IconName } from '@/components/ui';
import { timeAgo } from '@/utils/formatDate';

import { SyncJobProgress } from './SyncJobProgress';

const DRAFT_MESSAGE =
  'Add at least one rule before connecting this segment — an empty segment matches every contact.';

const PROVIDER_LABEL: Record<IntegrationType, string> = {
  brevo: 'Brevo',
  ecomail: 'Ecomail',
  mailchimp: 'Mailchimp',
};

type NoticeTone = 'amber' | 'red' | 'neutral';

const NOTICE_STYLE: Record<NoticeTone, { background: string; color: string; icon: IconName }> = {
  amber: { background: 'var(--amber-soft)', color: 'var(--amber)', icon: 'alert' },
  red: { background: 'var(--red-soft)', color: 'var(--red)', icon: 'alert' },
  neutral: { background: 'var(--surface-2, #f6f7f9)', color: 'var(--text-2)', icon: 'info' },
};

function Notice({ tone, children }: { tone: NoticeTone; children: ReactNode }) {
  const style = NOTICE_STYLE[tone];
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        padding: '8px 10px',
        background: style.background,
        color: style.color,
        border: tone === 'neutral' ? '1px solid var(--border)' : undefined,
        borderRadius: 'var(--radius-sm)',
        fontSize: 12.5,
        lineHeight: 1.45,
      }}
    >
      <Icon name={style.icon} size={14} style={{ marginTop: 2, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
}

function isAfter(a?: string, b?: string): boolean {
  if (!a) return false;
  if (!b) return true;
  return Date.parse(a) > Date.parse(b);
}

function syncSummary(ref: SegmentSyncRef): string {
  const parts = [
    ref.lastSyncAt ? `Last sync ${timeAgo(ref.lastSyncAt)}` : 'Not synced yet',
    ref.lastSyncCount != null && `${ref.lastSyncCount.toLocaleString()} contacts`,
    ref.lastSyncMode,
    ref.lastSyncStatus,
  ].filter(Boolean) as string[];
  return parts.join(' · ');
}

interface ConnectSegmentModalProps {
  open: boolean;
  onClose: () => void;
  segment: Segment;
  onRemoveFromProvider: (integrationId: string) => void;
}

export function ConnectSegmentModal({
  open,
  onClose,
  segment,
  onRemoveFromProvider,
}: ConnectSegmentModalProps) {
  const { data: integrations = [], isPending } = integrationsApi.useList();
  const { data: jobs = [] } = syncJobsApi.useActiveSyncJobs(segment.id, { pollWhenIdle: open });

  const isDraft = (segment.conditions?.conditions?.length ?? 0) === 0;
  const refs = segment.integrationSyncRefs ?? {};

  return (
    <ModalRoot open={open} onClose={onClose} size="lg" closeOnScrim>
      <div className="ds-modal-head">
        <h2>Connect to</h2>
        <IconButton icon="x" label="Close" className="ds-modal-close" onClick={onClose} />
      </div>
      <div className="ds-modal-body">
        <div className="ds-form-row">
          <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
            <strong>{segment.name}</strong> — {(segment.contactCount ?? 0).toLocaleString()}{' '}
            contacts
          </div>

          {isDraft && <Notice tone="amber">{DRAFT_MESSAGE}</Notice>}

          {isPending ? (
            <div style={{ color: 'var(--text-3)', fontSize: 13 }}>Loading integrations…</div>
          ) : integrations.length === 0 ? (
            <Notice tone="neutral">
              No integrations configured yet. Open{' '}
              <Link to="/settings/integrations" style={{ color: 'var(--brand)' }}>
                Settings → Integrations
              </Link>{' '}
              to connect a mailing service, then come back.
            </Notice>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {integrations.map((integration) => (
                <ConnectRow
                  key={integration.id}
                  segment={segment}
                  integration={integration}
                  syncRef={refs[integration.id]}
                  job={jobs.find(
                    (j) => j.integrationId === integration.id && isJobActive(j.status),
                  )}
                  isDraft={isDraft}
                  onRemove={() => onRemoveFromProvider(integration.id)}
                />
              ))}
            </div>
          )}

          <div style={{ fontSize: 12, color: 'var(--text-3)', lineHeight: 1.45 }}>
            Each provider gets a list named exactly <code>{segment.name}</code>. Auto-sync runs
            on the workspace schedule (hourly by default) and mirrors membership: it adds and
            updates matching contacts and removes contacts that leave the segment. Name, phone,
            birthday, profile fields and tags are synced; tags added by hand in the provider are
            kept.
          </div>
        </div>
      </div>
      <div className="ds-modal-foot">
        <span style={{ flex: 1 }} />
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      </div>
    </ModalRoot>
  );
}

interface ConnectRowProps {
  segment: Segment;
  integration: Integration;
  syncRef?: SegmentSyncRef;
  job?: SyncJob;
  isDraft: boolean;
  onRemove: () => void;
}

function ConnectRow({ segment, integration, syncRef, job, isDraft, onRemove }: ConnectRowProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const upsertLink = segmentLinksApi.useUpsertLink(segment.id);
  const unlink = segmentLinksApi.useUnlink(segment.id);
  const resync = segmentLinksApi.useResync(segment.id);
  const startSync = syncJobsApi.useStartSegmentSync(segment.id);

  const [connecting, setConnecting] = useState(false);
  const [confirmingUnlink, setConfirmingUnlink] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const provider = integration.name;
  const linked = !!syncRef;
  const autoSync = syncRef?.autoSync ?? false;
  const jobRunning = !!job;
  const keyProblem = hasKeyProblem(integration);
  const integrationDisabled = integration.status !== 'active';
  const usable = !keyProblem && !integrationDisabled;
  const mutating =
    connecting || upsertLink.isPending || unlink.isPending || resync.isPending || startSync.isPending;
  const fullResyncPending =
    linked && isAfter(syncRef.fullSyncRequestedAt, syncRef.lastFullSyncAt);

  const reattach = (e: unknown): boolean => {
    if (!conflictJobId(e)) return false;
    void queryClient.invalidateQueries({ queryKey: activeSyncJobsQueryKey(segment.id) });
    toast.show({
      severity: 'info',
      title: 'Already running',
      description: apiErrorMessage(e, `A job is already running for ${provider}.`),
    });
    return true;
  };

  const queueSync = async () => {
    await startSync.mutateAsync(integration.id);
    toast.show({
      severity: 'info',
      title: `Syncing ${segment.name} to ${provider}…`,
      description: `Queued a full sync of ${(segment.contactCount ?? 0).toLocaleString()} contacts.`,
    });
  };

  const connect = async () => {
    setError(null);
    setConnecting(true);
    try {
      await upsertLink.mutateAsync({ integrationId: integration.id, autoSync: false });
      await queueSync();
    } catch (e) {
      if (!reattach(e)) setError(apiErrorMessage(e, `Could not connect to ${provider}.`));
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    setError(null);
    try {
      await unlink.mutateAsync(integration.id);
      toast.show({
        severity: 'info',
        title: `Disconnected from ${provider}`,
        description: `The list stays in ${provider}.`,
      });
    } catch (e) {
      if (!reattach(e)) setError(apiErrorMessage(e, `Could not disconnect from ${provider}.`));
    } finally {
      setConfirmingUnlink(false);
    }
  };

  const changeAutoSync = async (next: boolean) => {
    setError(null);
    try {
      await upsertLink.mutateAsync({ integrationId: integration.id, autoSync: next });
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not change auto-sync.'));
    }
  };

  const syncNow = async () => {
    setError(null);
    try {
      await queueSync();
    } catch (e) {
      if (!reattach(e)) setError(apiErrorMessage(e, 'Could not start the sync.'));
    }
  };

  const requestFullResync = async () => {
    setError(null);
    try {
      await resync.mutateAsync(integration.id);
      toast.show({
        severity: 'info',
        title: 'Full re-sync requested',
        description: `The next scheduled run mirrors the whole segment to ${provider}.`,
      });
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not request a full re-sync.'));
    }
  };

  const connectDisabled =
    mutating || jobRunning || confirmingUnlink || (!linked && (isDraft || !usable));
  const autoSyncDisabled = !linked || mutating || (!autoSync && (isDraft || !usable));
  const syncNowDisabled = mutating || jobRunning || isDraft || !usable;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '10px 12px',
        background: 'var(--surface)',
        border: `1px solid ${linked ? 'var(--brand-soft-hover, var(--border))' : 'var(--border)'}`,
        borderRadius: 'var(--radius-sm)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>
            {provider}{' '}
            <span style={{ fontWeight: 400, color: 'var(--text-3)' }}>
              · {PROVIDER_LABEL[integration.type] ?? integration.type}
            </span>
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2 }}>
            {linked ? syncSummary(syncRef) : 'Not connected'}
          </div>
        </div>
        <Checkbox
          label="Connected"
          checked={linked || connecting}
          disabled={connectDisabled}
          onChange={(next) => {
            if (next) void connect();
            else setConfirmingUnlink(true);
          }}
        />
        <Checkbox
          label="Auto-sync"
          checked={autoSync}
          disabled={autoSyncDisabled}
          onChange={(next) => void changeAutoSync(next)}
        />
      </div>

      {linked && syncRef.lastSyncStatus === 'failed' && syncRef.lastSyncError && (
        <div style={{ fontSize: 12, color: 'var(--red)' }}>{syncRef.lastSyncError}</div>
      )}

      {linked && syncRef.autoSyncPausedReason && (
        <Notice tone="amber">Auto-sync paused: {syncRef.autoSyncPausedReason}</Notice>
      )}

      {fullResyncPending && (
        <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
          Full re-sync requested for the next scheduled run.
        </div>
      )}

      {keyProblem && (
        <Notice tone="red">
          API key problem: {integration.keyStatusMessage || 'the stored key cannot be used.'} Fix
          it in{' '}
          <Link to="/settings/integrations" style={{ color: 'inherit', fontWeight: 600 }}>
            Settings → Integrations
          </Link>
          .
        </Notice>
      )}

      {integrationDisabled && !keyProblem && (
        <Notice tone="neutral">This integration is disabled.</Notice>
      )}

      {job && <SyncJobProgress jobs={[job]} />}

      {confirmingUnlink && (
        <Notice tone="amber">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ flex: 1, minWidth: 200 }}>
              Stop syncing? The list stays in {provider}. To delete it there too, use Remove
              from {provider}… instead.
            </span>
            <Button
              size="sm"
              variant="danger"
              loading={unlink.isPending}
              onClick={() => void disconnect()}
            >
              Disconnect
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={unlink.isPending}
              onClick={() => setConfirmingUnlink(false)}
            >
              Cancel
            </Button>
          </div>
        </Notice>
      )}

      {error && <Notice tone="red">{error}</Notice>}

      {linked && !confirmingUnlink && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<Icon name="send" size={12} />}
            loading={startSync.isPending && !connecting}
            disabled={syncNowDisabled}
            title={isDraft ? DRAFT_MESSAGE : 'Run a full sync now'}
            onClick={() => void syncNow()}
          >
            Sync now
          </Button>
          {autoSync && (
            <Button
              size="sm"
              variant="ghost"
              leftIcon={<Icon name="refresh" size={12} />}
              loading={resync.isPending}
              disabled={mutating || isDraft || !usable || fullResyncPending}
              title="Mirror the whole segment on the next scheduled run instead of only the changes"
              onClick={() => void requestFullResync()}
            >
              Full re-sync
            </Button>
          )}
          <span style={{ flex: 1 }} />
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<Icon name="trash" size={12} />}
            disabled={mutating || jobRunning}
            style={{ color: 'var(--red)' }}
            onClick={onRemove}
          >
            Remove from {provider}…
          </Button>
        </div>
      )}
    </div>
  );
}
